import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FaBuilding, FaTools, FaPhoneAlt, FaCheckCircle, FaClock, FaSmile, FaUsers,
  FaStar, FaAward, FaHandshake, FaProjectDiagram, FaHardHat, FaThumbsUp
} from 'react-icons/fa';
import api, { getImageUrl } from '../services/api';
import { useSettings } from '../context/SettingsContext';
import useScrollReveal from '../hooks/useScrollReveal';
import Testimonials from '../components/Testimonials';
import BeforeAfter from '../components/BeforeAfter';

const ICON_MAP = {
  FaCheckCircle: <FaCheckCircle />,
  FaClock: <FaClock />,
  FaSmile: <FaSmile />,
  FaUsers: <FaUsers />,
  FaBuilding: <FaBuilding />,
  FaTools: <FaTools />,
  FaStar: <FaStar />,
  FaAward: <FaAward />,
  FaHandshake: <FaHandshake />,
  FaProjectDiagram: <FaProjectDiagram />,
  FaHardHat: <FaHardHat />,
  FaThumbsUp: <FaThumbsUp />,
};

function Home() {
  const { settings } = useSettings();
  const [sliders, setSliders] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [services, setServices] = useState([]);
  const [references, setReferences] = useState([]);
  const navigate = useNavigate();
  useScrollReveal();

  useEffect(() => {
    api.get('/sliders').then(res => setSliders(res.data)).catch(() => {});
    api.get('/services').then(res => setServices(res.data)).catch(() => {});
    api.get('/references').then(res => setReferences(res.data.slice(0, 8))).catch(() => {});
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % sliders.length);
  }, [sliders.length]);

  useEffect(() => {
    if (sliders.length <= 1) return;
    const interval = setInterval(nextSlide, 4000);
    return () => clearInterval(interval);
  }, [sliders.length, nextSlide]);

  return (
    <>
      <Helmet>
        <title>{settings.site_title || 'Ana Sayfa'}</title>
        <meta property="og:title" content={settings.site_title || 'Ana Sayfa'} />
        <meta property="og:description" content={settings.site_description || 'Profesyonel tadilat ve dekorasyon hizmetleri'} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        {settings.logo_path && <meta property="og:image" content={`${window.location.origin}${getImageUrl(settings.logo_path)}`} />}
      </Helmet>

      {/* Hero Slider */}
      {sliders.length > 0 && (
        <section className="hero-slider">
          {sliders.map((slide, index) => (
            <div
              key={slide.id}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
            >
              {slide.image_path && (
                <img src={getImageUrl(slide.image_path)} alt={slide.title || ''} />
              )}
              <div className="hero-overlay">
                {slide.title && <h1>{slide.title}</h1>}
                {slide.subtitle && <p>{slide.subtitle}</p>}
                {slide.button_text && slide.button_link && (
                  slide.button_link.startsWith('http') || slide.button_link.startsWith('tel:') || slide.button_link.startsWith('mailto:') ? (
                    <a href={slide.button_link} className="hero-btn">
                      {slide.button_text}
                    </a>
                  ) : (
                    <button className="hero-btn" onClick={() => navigate(slide.button_link)}>
                      {slide.button_text}
                    </button>
                  )
                )}
              </div>
            </div>
          ))}
          {sliders.length > 1 && (
            <div className="slider-dots">
              {sliders.map((_, index) => (
                <button
                  key={index}
                  className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => setCurrentSlide(index)}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Info Cards */}
      <section className="section">
        <div className="container">
          <div className="info-cards reveal">
            <div className="info-card">
              <div className="info-card-icon"><FaBuilding /></div>
              <h3>Kurumsal</h3>
              <p>Yılların deneyimi ve güvenilirliği ile hizmetinizdeyiz.</p>
              <Link to="/sayfa/hakkimizda" className="card-link">Detaylı Bilgi</Link>
            </div>
            <div className="info-card">
              <div className="info-card-icon"><FaTools /></div>
              <h3>Hizmetlerimiz</h3>
              <p>Profesyonel tadilat ve dekorasyon hizmetleri sunuyoruz.</p>
              <a href="#hizmetler" className="card-link">Hizmetlerimiz</a>
            </div>
            <div className="info-card">
              <div className="info-card-icon"><FaPhoneAlt /></div>
              <h3>Ücretsiz Keşif</h3>
              <p>Ücretsiz keşif hizmeti ile projelerinizi yerinde değerlendiriyoruz.</p>
              <Link to="/iletisim" className="card-link">Bize Ulasin</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Counters Section */}
      <CounterSection />

      {/* Services Grid */}
      {services.length > 0 && (
        <section className="section section-dark" id="hizmetler">
          <div className="container">
            <div className="section-title reveal">
              <h2>Hizmetlerimiz</h2>
              <p>Profesyonel tadilat ve dekorasyon hizmetlerimiz</p>
            </div>
            <div className="services-grid reveal">
              {services.map(service => (
                <Link to={`/hizmetler/${service.slug}`} key={service.id} className="service-card">
                  {service.images?.[0]?.image_path && (
                    <div className="service-card-icon">
                      <img src={getImageUrl(service.images[0].image_path)} alt={service.title} loading="lazy" />
                    </div>
                  )}
                  <h3>{service.title}</h3>
                  {service.short_description && <p style={{color:'#666',fontSize:'0.85rem',marginTop:'8px'}}>{service.short_description}</p>}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Before/After */}
      <BeforeAfter />

      {/* Testimonials */}
      <Testimonials />

      {/* References Preview */}
      {references.length > 0 && (
        <section className="section">
          <div className="container">
            <div className="section-title reveal">
              <h2>Referanslarımız</h2>
              <p>Tamamlanmış projelerimizden örnekler</p>
            </div>
            <div className="gallery-grid reveal">
              {references.map(ref => (
                <div key={ref.id} className="gallery-item">
                  <img src={getImageUrl(ref.image_path)} alt={ref.title || 'Referans'} loading="lazy" />
                  {ref.title && <div className="gallery-overlay"><span>{ref.title}</span></div>}
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <Link to="/referanslar" className="btn">Tüm Referanslar</Link>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

function useCountUp(target, duration = 2000, startCounting) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!startCounting) return;
    let start = 0;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, startCounting]);

  return count;
}

function CounterItem({ icon, targetValue, suffix, label, visible, delay }) {
  const count = useCountUp(targetValue, 2000, visible);
  return (
    <div className="counter-item" style={{ animationDelay: `${delay}ms` }}>
      <div className="counter-icon-wrap">
        <div className="counter-icon">{ICON_MAP[icon] || <FaCheckCircle />}</div>
      </div>
      <div className="counter-value">
        <span className="counter-number">{count}</span>
        <span className="counter-suffix">{suffix}</span>
      </div>
      <div className="counter-label">{label}</div>
    </div>
  );
}

function CounterSection() {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [counters, setCounters] = useState([]);

  useEffect(() => {
    api.get('/counters').then(res => setCounters(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (counters.length === 0) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [counters.length]);

  if (counters.length === 0) return null;

  return (
    <section className="counters-section" ref={sectionRef}>
      <div className="container">
        <div className="counters-grid">
          {counters.map((item, index) => (
            <CounterItem
              key={item.id}
              icon={item.icon}
              targetValue={item.value}
              suffix={item.suffix}
              label={item.label}
              visible={visible}
              delay={index * 150}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default Home;
